import { IPageSafeSpace, MicroFrontendPropsService } from '@adnoc/servicebox';
import { Inject, Injectable } from '@angular/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';

import { DeviceOsType } from '../enums';

@Injectable()
export class MobileService {
  private readonly safeSpaceMobileCssVariables = {
    bottom: '--servicebox--mobile--safe--space--bottom',
    left: '--servicebox--mobile--safe--space--left',
    right: '--servicebox--mobile--safe--space--right',
    top: '--servicebox--mobile--safe--space--top',
  };
  private readonly safeSpaceDesktopCssVariables = {
    left: '--servicebox--desktop--safe--space--left',
  };

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private deviceDetectorService: DeviceDetectorService,
    private microFrontendPropsService: MicroFrontendPropsService,
  ) {}

  isIOS(): boolean {
    return this.deviceDetectorService.os.toLowerCase() === DeviceOsType.ios;
  }

  /**
   * App has isMobile state if the application is used inside the OneHub mobile app
   *
   * @returns Observable that emits true if the application is used inside OneHub mobile app
   */
  isMobile$(): Observable<boolean> {
    return this.microFrontendPropsService.microFrontendProps$.pipe(
      map((microFrontendProps) => !!microFrontendProps.isMobile),
      distinctUntilChanged(),
    );
  }

  /**
   * App has isMobile state if the application is used inside the OneHub mobile app
   *
   * @returns true if the application is used inside OneHub mobile app
   */
  isMobile(): boolean {
    return !!this.microFrontendPropsService.snapshot.isMobile;
  }

  getSafeSpace(): IPageSafeSpace {
    const { bottom, left, top, right } = this.microFrontendPropsService.snapshot.mobilePageSafeArea ?? {};

    return {
      bottom: bottom ?? 0,
      left: left ?? 0,
      top: top ?? 0,
      right: right ?? 0,
    };
  }

  isMobileKeyboardOpen$(): Observable<boolean> {
    return this.microFrontendPropsService.microFrontendProps$.pipe(
      map((microFrontendProps) => !!microFrontendProps.isMobileKeyboardOpen),
      distinctUntilChanged(),
    );
  }

  isInsideApp$(): Observable<boolean> {
    return this.microFrontendPropsService.microFrontendProps$.pipe(
      map((microFrontendProps) => !!microFrontendProps.isMobile),
      distinctUntilChanged(),
    );
  }

  isInsideApp(): boolean {
    return !!this.microFrontendPropsService.snapshot.isMobile;
  }

  applyBottomSafeSpaceCssVariable(): void {
    this.setSafeAreas(this.safeSpaceMobileCssVariables, this.getSafeSpace());
    this.setSafeAreas(this.safeSpaceDesktopCssVariables, this.microFrontendPropsService.snapshot.desktopPageSafeArea);
  }

  private setSafeAreas(source: Partial<Record<keyof IPageSafeSpace, string>>, data: Partial<IPageSafeSpace>): void {
    Object.keys(source).forEach((side) => {
      this.document.documentElement.style.setProperty(
        source[side as keyof IPageSafeSpace] as string,
        `${data[side as keyof IPageSafeSpace]}px`,
      );
    });
  }
}
